// Screen Manager with navigation and persistence
import React, { useState, useEffect } from 'react';
import BitcoinClockWatch from './BitcoinClockWatch';
import BitcoinClockWatchTimeline from './BitcoinClockWatchTimeline';
import ClassicWatch from './ClassicWatch';
import ModernWatch from './ModernWatch';
import ColorfulWatch from './ColorfulWatch';

const ScreenManager = ({ btcPrice, blockHeight, stats, prevBlockTime, nextBlockTime }) => {
  const screens = [
    { id: 'bitcoin-clock', component: BitcoinClockWatch, name: 'Bitcoin Clock' },
    { id: 'bitcoin-timeline', component: BitcoinClockWatchTimeline, name: 'Block Timeline' },
    { id: 'classic', component: ClassicWatch, name: 'Classic' },
    { id: 'modern', component: ModernWatch, name: 'Modern' },
    { id: 'colorful', component: ColorfulWatch, name: 'Colorful' }
  ];

  // Get saved screen from localStorage or default to first screen
  const getSavedScreen = () => {
    const saved = localStorage.getItem('bitcoinClockScreen');
    const index = saved ? parseInt(saved) : 0;
    // Ensure the saved index is within bounds
    return index >= 0 && index < screens.length ? index : 0;
  };

  const [currentScreenIndex, setCurrentScreenIndex] = useState(getSavedScreen());
  const [showNavigation, setShowNavigation] = useState(false);

  // Save screen preference to localStorage
  useEffect(() => {
    localStorage.setItem('bitcoinClockScreen', currentScreenIndex.toString());
  }, [currentScreenIndex]);

  // Navigation functions
  const nextScreen = () => {
    setCurrentScreenIndex((prev) => (prev + 1) % screens.length);
  };

  const prevScreen = () => {
    setCurrentScreenIndex((prev) => (prev - 1 + screens.length) % screens.length);
  };

  // Touch/Swipe handling
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      nextScreen();
    }
    if (isRightSwipe) {
      prevScreen();
    }
  };

  // Current screen component
  const CurrentScreen = screens[currentScreenIndex].component;

  return (
    <div 
      style={{ 
        position: 'relative', 
        width: '100%', 
        height: '100vh',
        overflow: 'hidden'
      }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onMouseEnter={() => setShowNavigation(true)}
      onMouseLeave={() => setShowNavigation(false)}
    >
      {/* Current Screen */}
      <CurrentScreen 
        btcPrice={btcPrice} 
        blockHeight={blockHeight} 
        stats={stats}
        prevBlockTime={prevBlockTime}
        nextBlockTime={nextBlockTime}
      />

      {/* Navigation Arrows (Desktop) */}
      {showNavigation && (
        <React.Fragment>
          {/* Left Arrow */}
          <div
            onClick={prevScreen}
            style={{
              position: 'absolute',
              left: '20px',
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: 'rgba(0,0,0,0.5)',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              width: '60px',
              height: '60px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '24px',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              backdropFilter: 'blur(10px)',
              zIndex: 1000
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'rgba(0,0,0,0.7)';
              e.target.style.transform = 'translateY(-50%) scale(1.1)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'rgba(0,0,0,0.5)';
              e.target.style.transform = 'translateY(-50%) scale(1)';
            }}
          >
            ←
          </div>

          {/* Right Arrow */}
          <div
            onClick={nextScreen}
            style={{
              position: 'absolute',
              right: '20px',
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: 'rgba(0,0,0,0.5)',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              width: '60px',
              height: '60px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '24px',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              backdropFilter: 'blur(10px)',
              zIndex: 1000
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'rgba(0,0,0,0.7)';
              e.target.style.transform = 'translateY(-50%) scale(1.1)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'rgba(0,0,0,0.5)';
              e.target.style.transform = 'translateY(-50%) scale(1)';
            }}
          >
            →
          </div>
        </React.Fragment>
      )}

      {/* Screen Indicator Dots */}
      <div style={{
        position: 'absolute',
        bottom: '30px',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        gap: '10px',
        zIndex: 1000
      }}>
        {screens.map((_, index) => (
          <div
            key={index}
            onClick={() => setCurrentScreenIndex(index)}
            style={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              backgroundColor: index === currentScreenIndex 
                ? 'rgba(255,255,255,0.8)' 
                : 'rgba(255,255,255,0.3)',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              border: '1px solid rgba(255,255,255,0.5)'
            }}
          />
        ))}
      </div>

      {/* Swipe Instruction (Mobile) */}
      <div style={{
        position: 'absolute',
        top: '20px',
        right: '20px',
        fontSize: '0.7rem',
        color: 'rgba(255,255,255,0.5)',
        zIndex: 1000,
        textAlign: 'right'
      }}>
        {screens[currentScreenIndex].name}<br/>
        Swipe ← → to change
      </div>
    </div>
  );
};

export default ScreenManager;



// WEBPACK FOOTER //
// ./src/screens/ScreenManager.js